import React, { useEffect, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, formatRupiah } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function ReportsPage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/reports/summary?days=${days}`);
    setData(data);
  };

  useEffect(() => { load(); }, [days]);

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Laporan Penjualan</h1>
          <p className="text-[#5C6A61] mt-1">Analisis transaksi, item terjual, dan omzet</p>
        </div>
        <div className="flex gap-1 bg-white rounded-md border border-[#D3CEC4] p-1">
          {[7, 14, 30, 90].map((d) => (
            <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-md text-xs ${days === d ? "bg-[#1E3A2B] text-white" : "text-[#1E3A2B]"}`}>
              {d}h
            </button>
          ))}
        </div>
      </div>

      {!data && <div className="text-[#5C6A61]">Memuat...</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <Stat label="Jumlah Transaksi" value={data.transaction_count} testid="rep-tx" />
            <Stat label="Jumlah Item Terjual" value={data.total_items} testid="rep-items" />
            <Stat label="Total Omzet" value={formatRupiah(data.total_revenue)} testid="rep-rev" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-md border border-[#D3CEC4] p-6">
              <h3 className="font-display text-base font-semibold text-[#1E3A2B] mb-4">Omzet Harian</h3>
              <div className="w-full h-64" data-testid="chart-daily">
                <ResponsiveContainer>
                  <BarChart data={data.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DF" />
                    <XAxis dataKey="date" stroke="#5C6A61" fontSize={11} />
                    <YAxis stroke="#5C6A61" fontSize={11} tickFormatter={(v) => `Rp${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatRupiah(v)} />
                    <Bar dataKey="revenue" fill="#C25934" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white rounded-md border border-[#D3CEC4] p-6">
              <h3 className="font-display text-base font-semibold text-[#1E3A2B] mb-4">Menu Terlaris</h3>
              <div className="space-y-2" data-testid="top-items">
                {data.top_items.length === 0 && <div className="text-sm text-[#5C6A61]">Belum ada data.</div>}
                {data.top_items.map((it, idx) => (
                  <div key={it.name} className="flex items-center justify-between text-sm py-2 border-b border-[#EAE7DF] last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#EAE7DF] text-[#1E3A2B] text-xs flex items-center justify-center">{idx + 1}</span>
                      <div>
                        <div className="text-[#1E3A2B]">{it.name}</div>
                        <div className="text-xs text-[#5C6A61]">{it.quantity} terjual</div>
                      </div>
                    </div>
                    <div className="font-medium text-[#1E3A2B]">{formatRupiah(it.revenue)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </OwnerLayout>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-md border border-[#D3CEC4] p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">{label}</div>
      <div className="font-display text-2xl font-semibold text-[#1E3A2B] mt-2">{value}</div>
    </div>
  );
}
